import React, { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { Mark, Slider } from '@material-ui/core';
import { GroundType } from '../models/ground';
import { Maybe } from '../__generated__/graphql';
import { AthleteSessionType } from '../models/athlete_session';
import { OptionType } from '../components/Autocomplete';
import { ViewerDataTuple } from '../query/track';
import { MeType, UserUnitsType } from '../models/me';

export interface PlayersTeams {
  [k: string]: string[]
}

export type InfoPlayers = Array<string>;

export type BasicLocalCoords = { x: number, y: number };

export interface Vertices {
  A: BasicLocalCoords,
  B: BasicLocalCoords,
  C: BasicLocalCoords,
  D: BasicLocalCoords,
}

export interface RotationInfo {
  angle: number;
  centerX: number;
  centerY: number;
  isClockwise: boolean;
}

export interface PlayerPoint {
  x: number | null;
  x_0: number;
  y: number | null;
  y_0: number;
  scale: number;
  s: number | null;
  h: number | null;
  hr?: number | null;
}

export interface GetPlayerPoints {
  [k: string]: PlayerPoint
}

export interface PlayersTeamsContextValue {
  playersTeams: PlayersTeams;
  setPlayersTeams: React.Dispatch<React.SetStateAction<PlayersTeams>>;
}

export const STATUS_TEXT = {
  DATA_SESSION_LOADED: 'data: session loaded, computing...',
  INIT: 'starting...',
  PLAYERS_LOADED: 'players: loaded, computing...',
  PLAYERS_LOADING: 'players: loading...',
  PLAYERS_READY: 'players: ready!',
  SET_DRILLS: 'set: drills',
  SET_START_TIME: 'set: start time',
};

export interface WebPlayerProps {
  currentDrill: number | null;
  defaultGround?: boolean;
  isFullscreen?: boolean;
  sessionGround?: GroundType
  sessionId: string;
  showVertices?: boolean;
  teamId: string
  templateId: string;
}

export interface WebPlayerRecorderProps {
  currentDrill: number | null;
  defaultGround?: boolean;
  isFullscreen?: boolean;
  sessionGround?: GroundType
  sessionId: string;
  showVertices?: boolean;
  teamId: string
  templateId: string;
}

export interface SeriesPathDataValue {
  x: number | null;
  y: number | null;
  x_0?: number | null;
  y_0?: number | null;
  s?: number | null;
  h?: number | null;
  hr?: number | null;
}

export interface SeriesPathData {
  [t: string]: SeriesPathDataValue
}

export interface SeriesState {
  [k: string]: {
    pathData: SeriesPathData;
  };
}

export interface PlayersExtraData {[k: string]: {s: number | null, hr: number | null}}

export type DrillTimesType = { start: number; end: number; }[]
export type PlayersColors = Record<string, string>
export type PlayerDataInterval = [number, number];
export type PlayerDataIntervals = PlayerDataInterval[];

export type MarkerProps = {
  active: boolean;
  bearing?: number;
  color: string;
  cursor: number;
  extraData?: SeriesPathDataValue | null
  hover: boolean;
  id: string;
  onClick: (data: any) => void;
  onDblClick?: (data: any) => void;
  onMouseEnter?: (data: any) => void;
  onMouseLeave?: () => void;
  playerPoint: PlayerPoint;
  scale: number;
  showExtraData: boolean;
  text: string;
  textColor?: string;
  userUnits?: UserUnitsType;
};

export interface MarkDrill extends Mark {
  index?: Maybe<number>;
  id?: Maybe<number>;
  tags?: Maybe<string>[];
  idx?: number;
}

export interface PlayerControlsProps {
  currDrillRef: React.MutableRefObject<number>;
  currentDrill: number | null;
  currentTime: number;
  drillTimes: DrillTimesType;
  enabled: boolean;
  endTimestamp?: string;
  goToNextDrill: () => void;
  goToPrevDrill: () => void;
  handleChange: (event: ChangeEvent<{}> | null, time: number, background: boolean, isDrag: boolean) => void;
  handleFullscreenClick: () => void;
  handlePlayClick: () => void;
  handleRecordTrack?: () => void;
  intervals: PlayerDataIntervals;
  isRecording?: boolean;
  loadingInterval: PlayerDataInterval | null;
  marks: MarkDrill[];
  me: MeType;
  playerSpeed: number;
  playerStatus: 0 | 1;
  setPlayerSpeed: (n: number) => void;
  startTimestamp?: string;
  timeFormat: string;
  timestamps: number[];
  valueLabelFormat: React.ComponentProps<typeof Slider>['valueLabelFormat'];
}

export interface SliderDrillsProps {
  marks: MarkDrill[];
  min?: number;
  max?: number;
  currentTime: number;
  timestamps: number[];
}

export interface PlayerDetails {
  id: number;
  name: string;
  number: number | null;
  shortName: string;
}

export type PlayersDetails = Record<string, PlayerDetails>;

export interface DrawerPlayerDetailsProps {
  currentPlayer: null | number;
  playersDetails: PlayersDetails;
  status: string;
  setCurrentPlayer: Dispatch<SetStateAction<null | number>>;
  setPlayersColors: Dispatch<SetStateAction<Record<string, string>>>;
}

export type TeamColors = {teamA: string, teamB: string};

export interface GroundProps {
  currentActivePlayer: string | null;
  hoverPlayer: string | null;
  defaultPlayersColor: string;
  enabledPlayers: Array<string>;
  ground: GroundType;
  jerseyOrNumber: boolean;
  handlePlayerClick: (playerID: string) => void;
  handlePlayerEnter?: (playerID: string) => void;
  handlePlayerLeave?: () => void;
  infoPlayers: InfoPlayers;
  isLoadingData?: boolean;
  playerLinks: Set<[string, string]>;
  playersColors: PlayersColors;
  playersDetails: PlayersDetails;
  handleRemoveLink: (idx: number) => void;
  series: SeriesState;
  showVertices?: boolean;
  teamColors: TeamColors;
  trailsEnabled: boolean;
  userUnits?: UserUnitsType;
}

export const timestampSearchRange = 200; // ms, max cursor proximity range
export const markerTrailLength = 3000; // ms, trail length

export type PlayerLines = {
  start: [number, number],
  end: [number, number],
  distance: number,
}[]

export interface GroundFieldProps {
  activePlayer?: string | null
  hoverPlayer?: string | null
  children?: React.ReactNode;
  cursor: any;
  defaultPlayersColor?: string;
  enabledPlayers?: Array<string>;
  infoPlayers: InfoPlayers;
  jerseyOrNumber?: boolean;
  onPlayerClick?: (playerID: string) => void;
  onPlayerDblClick?: (playerID: string) => void;
  onPlayerEnter?: (playerID: string) => void;
  onPlayerLeave?: () => void;
  playersColors?: PlayersColors;
  playersDetails?: PlayersDetails;
  playersLines?: PlayerLines;
  playersPoints?: GetPlayerPoints;
  removeLink?: (idx: number) => void;
  series?: SeriesState;
  setZoom: React.Dispatch<React.SetStateAction<number>>;
  showVertices?: boolean;
  teamColors: TeamColors;
  trailsEnabled?: boolean;
  userUnits?: UserUnitsType;
}

export interface GroundGeometry {
  angle: number;
  scale: number;
  vertex: {
    vertexA: {
      x: number;
      y: number;
    },
    vertexC: {
      x: number;
      y: number;
    }
  };
  dims: {
    width: number;
    height: number;
  };
  center: {
    x: number;
    y: number;
  };
  scaledDims: {
    width: number;
    height: number;
  };
}

export interface GroundFieldData {
  dataGround?: GroundType;
  groundGeometry?: GroundGeometry;
  bearing?: number;
  offX: number;
  offY: number;
  scale: number;
  WINDOW_WIDTH: number;
  WINDOW_HEIGHT: number;
  zoom: number;
}

export interface PlayersLinkI {
  distance: number
  end: [number, number],
  start: [number, number]
}

export interface PlayersLinkProps {
  playersLines: PlayersLinkI[];
  removeLink: (id: number) => void;
  groundSurfaceColor?: string;
  scale: number;
}

export interface PlayersMarkersProps {
  activePlayer: string | null;
  bearing: number;
  cursor: number;
  defaultPlayersColor?: string;
  enabledPlayers: Array<string>;
  groundSurfaceColor?: string;
  hoverPlayer: string | null;
  infoPlayers: InfoPlayers;
  jerseyOrNumber: boolean;
  onPlayerClick: (playerID: string) => void;
  onPlayerDblClick?: (playerID: string) => void;
  onPlayerEnter?: (playerID: string) => void;
  onPlayerLeave?: () => void;
  playersColors: PlayersColors;
  playersDetails: PlayersDetails;
  playersPoints?: GetPlayerPoints;
  scale: number;
  series: SeriesState;
  teamColors: TeamColors;
  trailsEnabled: boolean;
  userUnits?: UserUnitsType;
  playersTeams: PlayersTeams;
}

type StatusKeys = keyof typeof STATUS_TEXT;
type Status = typeof STATUS_TEXT[StatusKeys];

export interface PlayersTableProps {
  activeAthSessions: Array<string>;
  athleteSessions: AthleteSessionType[];
  enabledPlayers: Array<string>;
  groundSurfaceColor: string;
  infoPlayers: InfoPlayers;
  jerseyOrNumber: boolean;
  loadingPlayers: Array<string>;
  playersColors: Record<string, string>;
  playersDetails: PlayersDetails;
  playersExtraData: PlayersExtraData;
  setInfoPlayers: React.Dispatch<React.SetStateAction<InfoPlayers>>;
  setEnabledPlayers: React.Dispatch<React.SetStateAction<Array<string>>>
  setPlayersColors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  status: Status;
  teamColors: TeamColors;
  userUnits?: UserUnitsType;
}

export interface WebPlayerHeaderProps {
  ground?: GroundType;
  groundOptions: OptionType[];
  groundSet: GroundType[];
  isSessionDataReady: boolean;
  jerseyOrNumber: boolean;
  setGround: React.Dispatch<React.SetStateAction<GroundType | undefined>>;
  setJerseyOrNumber: React.Dispatch<React.SetStateAction<boolean>>;
  setTrailsEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  trailsEnabled: boolean;
}

export type PathType = {
  [k: string]: SeriesPathDataValue
};

export type PathProps = {
  pathData: PathType;
  cursor: number;
  scale?: number;
  pathColor?: string;
};

export interface GPSGroundProps {
  activePlayer: string | null;
  hoverPlayer: string | null;
  defaultPlayersColor?: string;
  enabledPlayers: Array<string>;
  groundId: string;
  infoPlayers: InfoPlayers;
  jerseyOrNumber: boolean;
  onPlayerClick: (playerID: string) => void;
  onPlayerEnter?: (playerID: string) => void;
  onPlayerLeave?: () => void;
  playerLinks: Set<[string, string]>;
  playersColors: PlayersColors;
  playersDetails: PlayersDetails;
  removeLink: (idx: number) => void;
  series: SeriesState;
  showGroundName?: boolean;
  showVertices?: boolean;
  teamColors: TeamColors
  trailsEnabled: boolean;
  userUnits?: UserUnitsType;
}

export interface LPSGroundProps {
  activePlayer: string | null;
  hoverPlayer: string | null;
  defaultPlayersColor?: string;
  enabledPlayers: Array<string>;
  groundId: string;
  infoPlayers: InfoPlayers;
  jerseyOrNumber: boolean;
  onPlayerClick: (playerID: string) => void;
  onPlayerEnter?: (playerID: string) => void;
  onPlayerLeave?: () => void;
  playerLinks: Set<[string, string]>;
  playersColors: PlayersColors;
  playersDetails: PlayersDetails;
  removeLink: (idx: number) => void;
  series: SeriesState;
  showGroundName?: boolean;
  showVertices?: boolean;
  teamColors: TeamColors
  trailsEnabled: boolean;
  userUnits?: UserUnitsType;
}

export interface ZoomControlsProps {
  setZoom: React.Dispatch<React.SetStateAction<number>>
  zoom: number;
}

export interface WebPlayerWorkerProps {
  action: 'loadPathData' | 'loadMultiplePathData' | 'abort' | 'requireInit';
  drill?: null | number;
  jwtToken?: string;
  maxSamples?: number;
  athleteSessionId?: string;
  athleteSessionIds?: string[];
  start?: number;
  end?: number;
  computeData?: {
    currentGround?: GroundType;
    ground?: GroundType;
    groundDetails: false | GroundType | undefined;
    defaultGround: boolean | undefined;
  }
}

export interface WebplayerWorkerNew extends Omit<Worker, 'postMessage'> {
  postMessage(command: WebPlayerWorkerProps): void;
}

export interface WebPlayerWorkerResponse {
  type: 'loadedPathData' | 'ready' | 'error';
  serie: {
    id: string,
    drill: number | null,
    data: ViewerDataTuple[],
    intervals: PlayerDataIntervals,
  };
  error?: string;
  errorData?: {
    drill: null | number;
    id: string;
  };
}

export interface GetPlayersPointsLPSProps {
  cursor: number;
  enabledPlayers: Array<string>;
  scale: number,
  series: SeriesState;
  angle: number;
  center: { x: number, y: number };
  isCW: boolean;
}

export interface GetPlayersPointsGPSProps {
  cursor: number;
  enabledPlayers: Array<string>;
  scale: number,
  series: SeriesState;
  bearing: number;
  groundData: GroundType | null;
}

export interface MarkerTooltipProps {
  x: number;
  y: number;
  width: number;
  textLines: string[];
}

export interface MarkerHandlers {
  onClick: () => void;
  onDblClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}
export interface MarkerWithHeadingProps {
  bearing: number;
  position: SeriesPathDataValue;
  color: string;
  posX: number;
  posY: number;
  active: boolean;
  hover: boolean;
  handlers: MarkerHandlers;
}

export interface CircleMarkerProps {
  color: string;
  active?: boolean;
  hover?: boolean;
  handlers: MarkerHandlers;
  posX: number;
  posY: number;
}

export type FpsRecordCallback = (data: { time: number, frame: number }) => Promise<void>;
